import { useState } from 'react';
import { ScrollReveal } from './ScrollReveal';
import { api } from '@/lib/api';
import { FileDown, CheckCircle, ArrowRight } from 'lucide-react';

export default function WhitepaperGate() {
  const [form, setForm] = useState({ full_name: '', email: '', company_name: '', honeypot: '' });
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email) return;
    setLoading(true);
    try {
      await api.createLead({
        ...form,
        main_interest: 'Enterprise Architecture Whitepaper',
        source: 'whitepaper_download',
        source_section: 'whitepaper_gate',
        consent_contact: true,
      });
      setStatus('success');
    } catch { setStatus('error'); }
    setLoading(false);
  };

  return (
    <section className="relative py-20 sm:py-28 overflow-hidden" data-testid="whitepaper-section">
      <div className="absolute inset-0 bg-gradient-to-b from-aurocle-base via-aurocle-surface/30 to-aurocle-base" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-aurocle-gold/4 rounded-full blur-[200px] pointer-events-none" />

      <div className="relative max-w-5xl mx-auto px-6 lg:px-8">
        <ScrollReveal>
          <div className="glass-card border-aurocle-gold/10 overflow-hidden relative flash-sweep">
            <div className="grid lg:grid-cols-2 gap-0">
              {/* Left: Content */}
              <div className="p-10 lg:p-12 flex flex-col justify-center">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center border border-aurocle-gold/20">
                    <FileDown size={18} className="text-aurocle-gold" />
                  </div>
                  <span className="pill pill-gold text-[10px]">Premium Download</span>
                </div>
                <h3 className="font-heading text-2xl sm:text-3xl font-medium text-aurocle-text mb-3 tracking-tight">
                  Enterprise Architecture Whitepaper
                </h3>
                <p className="text-sm text-aurocle-platinum-muted leading-relaxed mb-6">
                  A comprehensive guide to structuring a private multi-agent workforce across departments, entities, and leadership layers. Includes architecture blueprints, governance frameworks, and deployment models.
                </p>
                <div className="space-y-2">
                  {['Architecture design patterns for multi-agent deployment', 'Governance and approval workflow frameworks',
                    'Industry-specific deployment strategies', 'Executive visibility and control models',
                    'Pilot to enterprise scaling roadmap'].map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-aurocle-text">
                      <div className="w-1 h-1 rounded-full bg-aurocle-gold shrink-0" />{item}
                    </div>
                  ))}
                </div>
              </div>

              {/* Right: Form or Success */}
              <div className="p-10 lg:p-12 bg-aurocle-base/40 flex items-center">
                {status === 'success' ? (
                  <div className="text-center w-full" data-testid="whitepaper-success">
                    <CheckCircle size={40} className="text-aurocle-gold mx-auto mb-4" />
                    <h4 className="font-heading text-lg text-aurocle-text mb-2">Request Received</h4>
                    <p className="text-sm text-aurocle-platinum-muted mb-4">Your whitepaper request has been sent to our team. We will deliver the Enterprise Architecture Whitepaper to your email and may follow up to explore how this architecture could be structured around your business.</p>
                    <p className="text-xs text-aurocle-text-secondary mb-6">Confirmation sent to <a href="mailto:info@aurocle.world" className="text-aurocle-gold hover:underline">info@aurocle.world</a></p>
                    <a href="mailto:info@aurocle.world?subject=Whitepaper%20Request%20-%20Aurocle%20Agents" className="btn-gold text-xs" data-testid="wp-email-link">
                      Contact info@aurocle.world <ArrowRight size={12} />
                    </a>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="w-full space-y-4" data-testid="whitepaper-form">
                    <div className="text-xs font-mono uppercase tracking-wider text-aurocle-gold/60 mb-2">Download Now</div>
                    <input type="text" name="hp" value={form.honeypot} onChange={e => setForm({...form, honeypot: e.target.value})} className="hidden" tabIndex={-1} />
                    <div>
                      <label className="premium-label">Full Name *</label>
                      <input type="text" required value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})}
                        className="premium-input" data-testid="wp-input-name" placeholder="Your full name" />
                    </div>
                    <div>
                      <label className="premium-label">Work Email *</label>
                      <input type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})}
                        className="premium-input" data-testid="wp-input-email" placeholder="you@company.com" />
                    </div>
                    <div>
                      <label className="premium-label">Company</label>
                      <input type="text" value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})}
                        className="premium-input" data-testid="wp-input-company" placeholder="Your company" />
                    </div>
                    <button type="submit" disabled={loading} className="btn-gold cta-burning w-full" data-testid="wp-submit-btn">
                      <FileDown size={14} /> {loading ? 'Processing...' : 'Download Whitepaper'}
                    </button>
                    <p className="text-[10px] text-aurocle-text-secondary text-center">
                      By downloading, you agree to be contacted regarding Aurocle Agents.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}
